import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { investigationService, monitorService } from '../services/api';
import { formatDateShort } from '../services/dateUtils';
import Graph from '../components/Graph';
import NotesPanel from '../components/NotesPanel';
import TagsPanel from '../components/TagsPanel';
import ExportPanel from '../components/ExportPanel';
import ImportPanel from '../components/ImportPanel';
import AIPanel from '../components/AIPanel';
import TxLookupPanel from '../components/TxLookupPanel';
import HopTracerPanel from '../components/HopTracerPanel';
import InvestigationStatementsPanel from '../components/InvestigationStatementsPanel';
import IntakeFormPanel from '../components/IntakeFormPanel';
import CollaboratorsPanel from '../components/CollaboratorsPanel';
import PatternPanel from '../components/PatternPanel';
import DefiPanel from '../components/DefiPanel';
import ClusteringPanel from '../components/ClusteringPanel';
import WalletMonitorPanel from '../components/WalletMonitorPanel';
import '../styles/InvestigationDetail.css';

export default function InvestigationDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { token } = useAuthStore();

  const [investigation, setInvestigation] = useState(null);
  const [graphData, setGraphData]         = useState(null);
  const [transactions, setTransactions]   = useState([]);
  const [notes, setNotes]                 = useState([]);
  const [tags, setTags]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [fetching, setFetching]           = useState(false);
  const [graphRefreshing, setGraphRefreshing] = useState(false); // silent bg refresh from monitor
  const [monitorAutoRefresh, setMonitorAutoRefresh] = useState(false);
  const [cascadeEnabled, setCascadeEnabled] = useState(false);
  const [cascadeDepth, setCascadeDepth]     = useState(2);
  const [monitoredAddresses, setMonitoredAddresses] = useState(new Set()); // for graph highlight
  const [deleting, setDeleting]           = useState(false);
  const [error, setError]                 = useState(null);
  const [activeTab, setActiveTab]         = useState('graph');
  const [hopTraceFocusAddress, setHopTraceFocusAddress] = useState(null);
  const [selectedGraphNode, setSelectedGraphNode] = useState(null);
  const [selectedGraphEdge, setSelectedGraphEdge] = useState(null);
  const [txFilter, setTxFilter] = useState('all'); // 'all' | 'incoming' | 'outgoing'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [clusterResult, setClusterResult] = useState(null); // persists across Clusters <-> Graph tab switches
  const crimeWindowRef = useRef(null);

  // ── Persistent graph view state (hidden edges + nodes) ──
  const [hiddenEdges, setHiddenEdges] = useState(new Set());
  const [hiddenNodes, setHiddenNodes] = useState(new Set());
  const [saveStatus, setSaveStatus]   = useState(null); // null | 'saving' | 'saved'
  const saveTimerRef = useRef(null);

  // ── Load everything needed for this investigation ──
  const loadInvestigation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [invRes, graphRes, notesRes, tagsRes, viewStateRes] = await Promise.all([
        investigationService.get(id),
        investigationService.getGraph(id),
        investigationService.getNotes(id),
        investigationService.getTags(id),
        investigationService.getGraphViewState(id),
      ]);

      setInvestigation(invRes.data);
      setGraphData(graphRes.data);
      setNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      setTags(Array.isArray(tagsRes.data)   ? tagsRes.data  : []);

      // Load monitored addresses for graph highlighting
      try {
        const monRes = await monitorService.list(invRes.data.id);
        const addrs = (monRes.data.monitors || []).map(m => m.address).filter(Boolean);
        setMonitoredAddresses(new Set(addrs));
      } catch (_) { /* non-critical — graph still works without this */ }

      // Restore persisted view state
      const vs = viewStateRes.data || {};
      const restoredEdges = new Set(vs.hidden_edges || []);
      const restoredNodes = new Set(vs.hidden_nodes || []);
      setHiddenEdges(restoredEdges);
      setHiddenNodes(restoredNodes);

      // Load crime window from seed TX data
      try {
        const seedRes = await investigationService.getTxLookupGraph(invRes.data.id);
        const txs = seedRes.data?.transactions || [];
        if (txs.length > 0) {
          const timestamps = txs
            .map(tx => tx.timestamp ? new Date(tx.timestamp).getTime() : null)
            .filter(Boolean);
          if (timestamps.length) {
            const windowStart = new Date(Math.min(...timestamps)).toISOString();
            const windowEnd   = new Date(Math.max(...timestamps) + 30 * 24 * 3600 * 1000).toISOString();
            crimeWindowRef.current = { start: windowStart, end: windowEnd };
          }
        }
      } catch (_) { /* no seed data yet — fine */ }

      // Derive flat transaction list from graph edges for export
      if (graphRes.data?.edges) {
        setTransactions(graphRes.data.edges.map((e) => ({
          tx_hash:      e.tx_hash     || e.id || '',
          from_address: e.from        || '',
          to_address:   e.to          || '',
          value:        e.value       || 0,
          token_symbol: e.token_symbol || (e.title ? e.title.split(' ')[1] || '' : ''),
          gas_price:    0,
          block_number: 0,
          timestamp:    e.timestamp   || '',
          status:       e.status      || 'success',
        })));
      }
    } catch (err) {
      console.error('Failed to load investigation:', err);
      setError('Failed to load investigation');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadInvestigation();
  }, [id, token, navigate, loadInvestigation]);

  // Clear the selected-node/edge sidebar cards when navigating away from the Graph tab -
  // Graph unmounts on tab switch, so its own state can't tell us this directly.
  useEffect(() => {
    if (activeTab !== 'graph') { setSelectedGraphNode(null); setSelectedGraphEdge(null); setTxFilter('all'); setDateFrom(''); setDateTo(''); }
  }, [activeTab]);

  // Reset TX direction filter whenever the selected node changes (including when it clears)
  useEffect(() => {
    setTxFilter('all');
  }, [selectedGraphNode]);

  // ── Debounced auto-save of view state ──
  const triggerAutoSave = useCallback((edges, nodes) => {
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await investigationService.saveGraphViewState(id, edges, nodes);
        setSaveStatus('saved');
        // Clear 'saved' indicator after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } catch (e) {
        console.error('Auto-save failed:', e);
        setSaveStatus(null);
      }
    }, 1500);
  }, [id]);

  // ── Called by Graph when investigator hides/restores edges or nodes ──
  const handleViewStateChange = useCallback((newHiddenEdges, newHiddenNodes) => {
    setHiddenEdges(new Set(newHiddenEdges));
    setHiddenNodes(new Set(newHiddenNodes));
    triggerAutoSave(new Set(newHiddenEdges), new Set(newHiddenNodes));
  }, [triggerAutoSave]);

  // ── Fetch live blockchain data ──
  const handleFetchBlockchainData = async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await investigationService.fetchBlockchain(id);
      if (response.data.graph) {
        setGraphData(response.data.graph);
        if (response.data.graph.edges) {
          setTransactions(response.data.graph.edges.map((e) => ({
            tx_hash:      e.tx_hash     || e.id || '',
            from_address: e.from        || '',
            to_address:   e.to          || '',
            value:        e.value       || 0,
            token_symbol: e.token_symbol || (e.title ? e.title.split(' ')[1] || '' : ''),
            gas_price:    0,
            block_number: 0,
            timestamp:    e.timestamp   || '',
            status:       e.status      || 'success',
          })));
        }
      }
    } catch (err) {
      console.error('Failed to fetch blockchain data:', err);
      setError('Failed to fetch blockchain data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFetching(false);
    }
  };

  // ── Silent graph-only refresh (called by monitor auto-refresh — reads saved graph_data, no Etherscan call) ──
  const refreshGraphSilently = useCallback(async () => {
    try {
      setGraphRefreshing(true);
      // fetchBlockchain re-hits Etherscan AND saves to DB; getGraph just reads the saved graph_data.
      // We call fetchBlockchain so new TXs are actually pulled in, but we do NOT setLoading.
      const response = await investigationService.fetchBlockchain(id);
      if (response.data?.graph) {
        setGraphData(response.data.graph);
        if (response.data.graph.edges) {
          setTransactions(response.data.graph.edges.map((e) => ({
            tx_hash:      e.tx_hash     || e.id || '',
            from_address: e.from        || '',
            to_address:   e.to          || '',
            value:        e.value       || 0,
            token_symbol: e.token_symbol || (e.title ? e.title.split(' ')[1] || '' : ''),
            gas_price:    0,
            block_number: 0,
            timestamp:    e.timestamp   || '',
            status:       e.status      || 'success',
          })));
        }
      }
    } catch (err) {
      // Swallow silently — monitor auto-refresh should never interrupt the investigator.
      // They can manually click Fetch Blockchain Data if needed.
      console.warn('[monitor] Silent graph refresh failed:', err?.response?.data?.detail || err.message);
    } finally {
      setGraphRefreshing(false);
    }
  }, [id]);

  // ── Reload graph after an investigation-scoped statement upload merges new
  // txs into it (routers/investigation_statements.py already saved graph_data
  // server-side -- this just re-reads it, no new blockchain fetch needed) ──
  const handleStatementGraphUpdated = useCallback(async () => {
    try {
      const res = await investigationService.getGraph(id);
      setGraphData(res.data);
      if (res.data?.edges) {
        setTransactions(res.data.edges.map((e) => ({
          tx_hash:      e.tx_hash     || e.id || '',
          from_address: e.from        || '',
          to_address:   e.to          || '',
          value:        e.value       || 0,
          token_symbol: e.token_symbol || (e.title ? e.title.split(' ')[1] || '' : ''),
          gas_price:    0,
          block_number: 0,
          timestamp:    e.timestamp   || '',
          status:       e.status      || 'success',
        })));
      }
    } catch (err) {
      console.warn('[statements] Failed to reload graph after upload:', err?.response?.data?.detail || err.message);
    }
  }, [id]);

  // ── Open TX trace window ──
  const handleEdgeClick = useCallback((tx) => {
    const recipient = tx.to || tx.to_address || tx.target;
    if (!recipient) return;
    const cw = crimeWindowRef.current;
    const params = new URLSearchParams({
      from:  tx.from || tx.from_address || '',
      tx:    tx.tx_hash || '',
      hop:   '1',
      value: String(tx.value || ''),
      token: tx.token_symbol || '',
      invId: id,
      ...(cw?.start && { windowStart: cw.start }),
      ...(cw?.end   && { windowEnd:   cw.end   }),
    });
    window.open(
      `/trace/${investigation.blockchain}/${recipient}?${params.toString()}`,
      `trace_${recipient.slice(0, 12)}`,
    );
  }, [id, investigation]);

  // ── Open a TracePage window for a node clicked directly in the main graph ──
  const handleNodeTrace = useCallback((address) => {
    if (!address || !investigation) return;
    const cw = crimeWindowRef.current;
    const params = new URLSearchParams({
      from:  investigation.start_address || '',
      hop:   '1',
      invId: id,
      ...(cw?.start && { windowStart: cw.start }),
      ...(cw?.end   && { windowEnd:   cw.end   }),
    });
    window.open(
      `/trace/${investigation.blockchain}/${address}?${params.toString()}`,
      `trace_${address.slice(0, 12)}`,
    );
  }, [id, investigation]);

  // ── Switch to Hop Tracer tab with this node pre-filled as the new trace root ──
  const handleNodeHopTrace = useCallback((address) => {
    if (!address) return;
    setHopTraceFocusAddress(address);
    setActiveTab('hoptrace');
  }, []);

  // ── Delete investigation ──
  const handleDeleteInvestigation = async () => {
    if (!window.confirm(`Delete "${investigation?.title}"? This cannot be undone.`)) return;
    try {
      setDeleting(true);
      await investigationService.delete(id);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Failed to delete investigation');
      setDeleting(false);
    }
  };

  if (loading)        return <div className="loading-container">Loading investigation...</div>;
  if (!investigation) return <div className="error-container">Investigation not found</div>;

  const tabs = [
    { id: 'graph',     label: '🔗 Graph'    },
    { id: 'txlookup',  label: '🌱 Seed TXs'  },
    { id: 'statements', label: '📄 Statements' },
    { id: 'intake',     label: '📋 Intake'     },
    { id: 'notes',     label: '📝 Notes'    },
    { id: 'tags',      label: '🏷️ Tags'     },
    { id: 'import',    label: '📂 Import'   },
    { id: 'export',    label: '📥 Export'   },
    { id: 'ai',        label: '🤖 AI'       },
    { id: 'patterns',  label: '🔍 Patterns'  },
    { id: 'defi',      label: '🧬 DeFi'      },
    { id: 'clusters',  label: '🧩 Clusters'  },
    { id: 'hoptrace',  label: '🕸️ Hop Tracer' },
    { id: 'monitor',   label: '📡 Monitor'   },
    ...(investigation.is_owner || investigation.my_role === 'admin'
      ? [{ id: 'share', label: '👥 Share' }] : []),
  ];

  const hiddenEdgeCount = hiddenEdges.size;
  const hiddenNodeCount = hiddenNodes.size;

  return (
    <div className="investigation-detail">
      <header className="detail-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          ← Back
        </button>
        <div className="header-title">
          <h1>{investigation.title}</h1>
          <span className={`status-badge ${investigation.status}`}>
            {investigation.status}
          </span>
        </div>
        <div className="header-stats">
          <div className="header-stat">
            <strong>{graphData?.nodes?.length || 0}</strong>
            <label>Addresses</label>
          </div>
          <div className="header-stat">
            <strong>{graphData?.edges?.length || 0}</strong>
            <label>Transactions</label>
          </div>
          <div className="header-stat">
            <strong>{notes.length}</strong>
            <label>Notes</label>
          </div>
          <div className="header-stat">
            <strong>{tags.length}</strong>
            <label>Tags</label>
          </div>
        </div>
        {(investigation.is_owner || investigation.my_role === 'admin') && (
          <button onClick={handleDeleteInvestigation} disabled={deleting} className="delete-btn">
            {deleting ? 'Deleting...' : '🗑️ Delete'}
          </button>
        )}
      </header>

      <main className="detail-content">
        <aside className="detail-sidebar">
          <div className="info-card">
            <h3>Details</h3>
            <div className="info-item">
              <label>Blockchain</label>
              <strong>{investigation.blockchain?.toUpperCase()}</strong>
            </div>
            <div className="info-item">
              <label>Start Address</label>
              <code className="address-code">{investigation.start_address}</code>
            </div>
            <div className="info-item">
              <label>Created</label>
              <span>{formatDateShort(investigation.created_at)}</span>
            </div>
            <div className="info-item">
              <label>Updated</label>
              <span>{formatDateShort(investigation.updated_at)}</span>
            </div>
          </div>

          {investigation.description && (
            <div className="info-card">
              <h3>Description</h3>
              <p>{investigation.description}</p>
            </div>
          )}

          <div className="action-card">
            {investigation.my_role !== 'viewer' ? (
              <button onClick={handleFetchBlockchainData} disabled={fetching} className="fetch-btn">
                {fetching ? '⏳ Fetching...' : '🔍 Fetch Blockchain Data'}
              </button>
            ) : (
              <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
                You have view-only access to this investigation.
              </p>
            )}
            {error && <div className="error-message">{error}</div>}
          </div>

          {/* Selected node — mirrors whatever's selected on the Graph tab's canvas.
              This sidebar card is the ONLY place node details are shown; there is no
              in-canvas floating panel (it would get clipped by the graph canvas's
              overflow:hidden, and a side panel is simpler anyway). */}
          {activeTab === 'graph' && selectedGraphNode && (
            <div className="info-card" style={{ borderColor: 'rgba(0,217,255,0.3)' }}>
              <h3>🎯 Selected Address</h3>
              <code className="address-code" style={{ marginBottom: 12 }}>
                {selectedGraphNode.id}
              </code>
              <div className="stats-grid-small" style={{ marginBottom: 12 }}>
                <div className="stat-item">
                  <label>Risk Score</label>
                  <strong>{(selectedGraphNode._raw?.risk_score || 0).toFixed(1)}/10</strong>
                </div>
                <div className="stat-item">
                  <label>Transactions</label>
                  <strong>{selectedGraphNode._raw?.tx_count || 0}</strong>
                </div>
              </div>

              {/* TX direction filter segmented control */}
              <div className="tx-filter-control">
                {[['all', 'All TX'], ['incoming', '↓ Incoming'], ['outgoing', '↑ Outgoing']].map(([val, label]) => (
                  <button
                    key={val}
                    className={`tx-filter-btn${txFilter === val ? ' active' : ''}`}
                    onClick={() => setTxFilter(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {selectedGraphNode.id !== investigation.start_address && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => handleNodeHopTrace(selectedGraphNode.id)} className="fetch-btn">
                    🕸️ Create Hop From This Address
                  </button>
                  <button onClick={() => handleNodeTrace(selectedGraphNode.id)} className="fetch-btn"
                    style={{ background: 'rgba(0,217,255,0.12)', color: '#00d9ff' }}>
                    🔍 Trace This Wallet →
                  </button>
                  <button
                    onClick={() => {
                      const nodeId = selectedGraphNode.id;
                      const newNodes = new Set([...hiddenNodes, nodeId]);
                      setSelectedGraphNode(null);
                      handleViewStateChange(hiddenEdges, newNodes);
                    }}
                    className="fetch-btn"
                    style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171' }}
                    title="Remove this address and all its transactions from the investigation graph"
                  >
                    🗑 Remove Node
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Selected edge — mirrors whatever transaction is selected on the Graph tab's canvas.
              Same rationale as the node card above: lives in the sidebar, not in an in-canvas
              floating panel that gets clipped by the graph canvas's overflow:hidden. */}
          {activeTab === 'graph' && selectedGraphEdge && (
            <div className="info-card" style={{ borderColor: 'rgba(170,200,255,0.3)' }}>
              <h3>⇒ Selected Transaction</h3>
              <div className="info-item">
                <label>From</label>
                <code className="address-code">{selectedGraphEdge.source}</code>
              </div>
              <div className="info-item">
                <label>To</label>
                <code className="address-code">{selectedGraphEdge.target}</code>
              </div>
              {selectedGraphEdge.tx_hash && (
                <div className="info-item">
                  <label>TX Hash</label>
                  <code className="address-code">{selectedGraphEdge.tx_hash}</code>
                </div>
              )}
              <div className="stats-grid-small" style={{ marginTop: 10, marginBottom: 12 }}>
                <div className="stat-item">
                  <label>Amount</label>
                  <strong>{selectedGraphEdge.value || 0} {selectedGraphEdge.token_symbol || ''}</strong>
                </div>
                {selectedGraphEdge.timestamp && (
                  <div className="stat-item">
                    <label>Date</label>
                    <strong style={{ fontSize: 12 }}>{formatDateShort(selectedGraphEdge.timestamp)}</strong>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button onClick={() => handleEdgeClick({
                  from:         selectedGraphEdge.source,
                  to:           selectedGraphEdge.target,
                  tx_hash:      selectedGraphEdge.tx_hash,
                  value:        selectedGraphEdge.value,
                  token_symbol: selectedGraphEdge.token_symbol,
                })} className="fetch-btn"
                  style={{ background: 'rgba(0,217,255,0.12)', color: '#00d9ff' }}>
                  🔍 Trace Recipient Wallet →
                </button>
                <button
                  onClick={() => {
                    const newEdges = new Set([...hiddenEdges, selectedGraphEdge.id]);
                    setSelectedGraphEdge(null);
                    handleViewStateChange(newEdges, hiddenNodes);
                  }}
                  className="fetch-btn"
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171' }}
                  title="Remove this transaction from the investigation graph"
                >
                  🗑 Remove TX
                </button>
              </div>
            </div>
          )}

          {/* Hidden items summary */}
          {(hiddenEdgeCount > 0 || hiddenNodeCount > 0) && (
            <div className="info-card" style={{ borderColor: 'rgba(239,68,68,0.3)' }}>
              <h3 style={{ color: '#f87171' }}>🙈 Hidden Items</h3>
              {hiddenEdgeCount > 0 && (
                <div className="info-item">
                  <label>Transactions</label>
                  <strong style={{ color: '#fca5a5' }}>{hiddenEdgeCount} removed</strong>
                </div>
              )}
              {hiddenNodeCount > 0 && (
                <div className="info-item">
                  <label>Addresses</label>
                  <strong style={{ color: '#fca5a5' }}>{hiddenNodeCount} hidden</strong>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                {saveStatus === 'saving' && '⏳ Saving…'}
                {saveStatus === 'saved' && '✓ Saved'}
                {saveStatus === null && 'Persisted across sessions'}
              </div>
            </div>
          )}

          {/* Auto-save status pill (when nothing is hidden but a save just happened) */}
          {saveStatus && hiddenEdgeCount === 0 && hiddenNodeCount === 0 && (
            <div style={{ fontSize: 11, color: '#6b7280', padding: '4px 8px' }}>
              {saveStatus === 'saving' ? '⏳ Saving…' : '✓ Graph view saved'}
            </div>
          )}
        </aside>

        <section className="detail-main">
          <div className="tab-bar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'graph' && (
              <div className="graph-container">
                {graphData?.nodes?.length > 0 ? (
                  <>
                    <Graph
                      data={graphData}
                      startAddress={investigation.start_address}
                      hiddenEdges={hiddenEdges}
                      hiddenNodes={hiddenNodes}
                      onViewStateChange={handleViewStateChange}
                      onNodeSelect={setSelectedGraphNode}
                      onEdgeSelect={setSelectedGraphEdge}
                      txFilter={txFilter}
                      onTxFilterChange={setTxFilter}
                      investigationId={id}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                      onDateFromChange={setDateFrom}
                      onDateToChange={setDateTo}
                      monitoredAddresses={monitoredAddresses}
                    />
                    <div className="graph-info">
                      Showing {graphData.nodes.length} addresses and {graphData.edges.length} transactions
                      {(hiddenEdgeCount > 0 || hiddenNodeCount > 0) && (
                        <span style={{ color: '#f87171', marginLeft: 8 }}>
                          ({hiddenEdgeCount > 0 ? `${hiddenEdgeCount} TX${hiddenEdgeCount !== 1 ? 's' : ''} removed` : ''}
                          {hiddenEdgeCount > 0 && hiddenNodeCount > 0 ? ', ' : ''}
                          {hiddenNodeCount > 0 ? `${hiddenNodeCount} address${hiddenNodeCount !== 1 ? 'es' : ''} hidden` : ''})
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="empty-graph">
                    <p>No transaction graph yet.</p>
                    <p className="hint">Click "Fetch Blockchain Data" to build the graph</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesPanel investigationId={id} />
            )}

            {activeTab === 'tags' && (
              <TagsPanel investigationId={id} />
            )}

            {activeTab === 'import' && (
              <ImportPanel
                investigationId={id}
                investigation={investigation}
                onImportComplete={loadInvestigation}
              />
            )}

            {activeTab === 'export' && (
              <ExportPanel
                investigation={investigation}
                transactions={transactions}
                notes={notes}
                tags={tags}
                graphData={graphData}
              />
            )}

            {activeTab === 'ai' && (
              <AIPanel
                investigationId={id}
                investigation={investigation}
              />
            )}

            {activeTab === 'txlookup' && (
              <TxLookupPanel investigation={investigation} />
            )}

            {activeTab === 'statements' && (
              <InvestigationStatementsPanel
                investigationId={id}
                onGraphUpdated={handleStatementGraphUpdated}
              />
            )}

            {activeTab === 'intake' && (
              <IntakeFormPanel investigationId={id} />
            )}

            {activeTab === 'hoptrace' && (
              <HopTracerPanel
                investigation={investigation}
                focusAddress={hopTraceFocusAddress}
                onFocusAddressConsumed={() => setHopTraceFocusAddress(null)}
              />
            )}

            {activeTab === 'patterns' && (
              <PatternPanel
                graphData={graphData}
                hiddenNodes={hiddenNodes}
                hiddenEdges={hiddenEdges}
                onViewStateChange={handleViewStateChange}
                onJumpToGraph={() => setActiveTab('graph')}
              />
            )}

            {activeTab === 'defi' && (
              <DefiPanel
                investigationId={id}
                investigation={investigation}
              />
            )}

            {activeTab === 'clusters' && (
              <ClusteringPanel
                investigationId={id}
                investigation={investigation}
                result={clusterResult}
                onResultChange={setClusterResult}
              />
            )}

            {activeTab === 'monitor' && (
              <WalletMonitorPanel
                investigationId={id}
                investigation={investigation}
                onNewTx={refreshGraphSilently}
                autoRefresh={monitorAutoRefresh}
                onAutoRefreshChange={setMonitorAutoRefresh}
                graphRefreshing={graphRefreshing}
                cascadeEnabled={cascadeEnabled}
                onCascadeEnabledChange={setCascadeEnabled}
                cascadeDepth={cascadeDepth}
                onCascadeDepthChange={setCascadeDepth}
                onMonitorsChange={addrs => setMonitoredAddresses(new Set(addrs))}
              />
            )}

            {activeTab === 'share' && (
              <CollaboratorsPanel investigationId={id} />
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
